import { URGENCY_SUPER_HOT_DAYS } from "@xop/dashboard-shared";

import { agentWindowIso, type AgentWindow } from "@/lib/today";

import type { ListLeadsParams } from "../../client.js";

/**
 * The lead-list status filters + the pure URL-tab normalizer and param builder,
 * extracted from `LeadsPage.tsx` so the deep-link mapping + filter→params logic
 * unit-test directly.
 */
export type LeadFilter = {
  key: string;
  label: string;
  status?: string;
  /** "Closing soon" — a deadline-urgency view rather than a pipeline status. */
  closing?: boolean;
  /** A recency view rather than a pipeline status: every lead (of ANY status,
   * including archived) created inside the window, newest first. "Today" is
   * the home of the old Today tab's "leads added today" list; the week / 7-day
   * / month windows sit beside it (2026-09-25). All share the agent's 09:00
   * Riga day boundary — see `agentWindowIso`. */
  window?: AgentWindow;
};

// A row of segmented PILLS collapsed to the 3-bucket model + two extra views:
//   • All (DEFAULT) — every lead, each row carrying its colored Status pill.
//   • Today · This week · Last 7 days · This month · Last month — every lead
//     (of ANY status) created in that window (`agentWindowIso`).
//   • New — freshly identified, awaiting review (`status=new`).
//   • Qualified — BOTH "Qualified" stages: `qualified` + `exported`.
//   • Rejected — `denied` (operator-declined) + `dismissed` (agent-archived).
//   • Closing soon — deadline-urgency view within the super-hot horizon.
export const STATUS_FILTERS: LeadFilter[] = [
  { key: "all", label: "All", status: "all" },
  { key: "today", label: "Today", window: "today" },
  { key: "week", label: "This week", window: "week" },
  { key: "last7", label: "Last 7 days", window: "last7" },
  { key: "month", label: "This month", window: "month" },
  { key: "last_month", label: "Last month", window: "last_month" },
  { key: "new", label: "New", status: "new" },
  { key: "qualified", label: "Qualified", status: "qualified,exported" },
  { key: "rejected", label: "Rejected", status: "denied,dismissed" },
  { key: "closing", label: "Closing soon", closing: true },
];

/** Map a `?tab=` deep-link value (the param predates the filter rework) onto a
 * current filter key, so existing links keep working: the important ones —
 * `qualified` (CRM cross-links), `closing` (dashboard pulse), and `today`
 * (`/sales/today` forwards to `/sales/leads?tab=today`) — pass through; retired
 * keys fold into the nearest bucket; anything unknown lands on "all". */
export function normalizeFilterKey(urlTab: string | null): string {
  switch (urlTab) {
    case "all":
    case "today":
    case "week":
    case "last7":
    case "month":
    case "last_month":
    case "new":
    case "qualified":
    case "rejected":
    case "closing":
      return urlTab;
    case "denied":
    case "dismissed":
      return "rejected";
    default:
      // active / contacted / unknown → the default All view.
      return "all";
  }
}

/** The list params a filter contributes — a pipeline `status`, the
 * `closing_within_days` deadline filter for the "Closing soon" view, or a
 * recency window for Today / This week / Last 7 days / This month / Last month:
 * `status=all` + `created_after` (+ `created_before` for the closed "Last
 * month") + `sort=created_at` (newest-added first). The bounds are absolute
 * instants, identical for every viewer. `created_after`/`created_before`
 * compose independently of `status` agent-side. */
export function filterParams(t: LeadFilter | undefined): Partial<ListLeadsParams> {
  const win = t?.window ? agentWindowIso(t.window) : null;
  return {
    ...(t?.status ? { status: t.status } : {}),
    ...(t?.closing ? { closing_within_days: URGENCY_SUPER_HOT_DAYS } : {}),
    ...(win
      ? {
          status: "all",
          created_after: win.after,
          ...(win.before ? { created_before: win.before } : {}),
          sort: "created_at",
        }
      : {}),
  };
}
